/**
 * OrderCard — Plenty-Stil-Karte fuer eine GroupedOrder (1 Karte pro Zeile).
 *
 * Phase 5:
 *   - 1 Karte pro Zeile mit allen Card-Feldern (Stammdaten + Adressen + Tags + Items inline)
 *   - Tab-spezifisches Layout: Spedition zeigt Alarm-Farbe (5W gelb, 6W rot)
 *   - Type-4-Kinder eingeklappt unter main
 */

import { useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Truck,
  MapPin,
  CreditCard,
  Tag,
  MessageSquare,
  Star,
  CheckCircle2,
} from 'lucide-react';
import { GroupedOrder, OrderListItem } from '../lib/groupOrders';
import { computeSpeditionAlarm } from '../lib/speditionAlarm';
import { api } from '@libero/core/services/api';
import {
  formatStuckInfo,
  formatSupplierStuckInfo,
  formatSupplierStatusSince,
  formatDurationHuman,
} from '../lib/stuckInfoFormatter';
import {
  cleanEventText,
  deriveSendungslageBadge,
  SENDUNGSLAGE_TONE_CLS,
} from '../lib/sendungslage';

interface OrderCardProps {
  group: GroupedOrder;
  // Legacy 'spedition' = neu 'lieferanten' (Phase 11). Beide Werte akzeptieren
  // damit der Alarm-Branch unten weiter greift.
  kategorie: 'paket' | 'spedition' | 'lieferanten' | 'followup' | 'all';
  onSelect: (id: number) => void;
  /** Phase 14: Callback wenn Followup-Toggle persistiert wurde (Parent kann reload triggern). */
  onFollowupToggled?: (orderId: number, marked: boolean) => void;
  /** Callback wenn Auftrag manuell abgeschlossen wurde (Parent triggert reload). */
  onClosed?: (orderId: number) => void;
  /** 20.08.2026: Geklärt-Haken anzeigen (nur im Rücksendungs-Reiter). */
  showGeklaertToggle?: boolean;
  /** 20.08.2026: Callback wenn der Geklärt-Haken persistiert wurde (Parent triggert reload). */
  onGeklaertToggled?: (orderId: number) => void;
}

/** Plenty-Status → supplierKey-Mapping (analog SUPPLIER_SECTION_STATUS_ID). */
function supplierKeyForStatus(orderStatusId: number | null | undefined): string | null {
  if (orderStatusId == null) return null;
  if (orderStatusId === 5.5)  return 'huck';
  if (orderStatusId === 5.6)  return 'helo';
  if (orderStatusId === 5.65) return 'glawel';
  if (orderStatusId === 5.66) return 'sporttore';
  return null;
}

const statusBadge: Record<string, { label: string; cls: string }> = {
  open: { label: 'Offen', cls: 'bg-gray-100 text-gray-700' },
  awaiting_package: { label: 'Wartet auf Paket', cls: 'bg-blue-50 text-blue-700' },
  carrier: { label: 'Bei Carrier', cls: 'bg-yellow-50 text-yellow-800' },
  manual_only: { label: 'Nur manuell', cls: 'bg-purple-50 text-purple-700' },
  done: { label: 'Abgeschlossen', cls: 'bg-green-50 text-green-700' },
  closed_manually: { label: 'Manuell geschlossen', cls: 'bg-gray-200 text-gray-500' },
};

const internalStatusLabel: Record<string, string> = {
  pre_transit: 'Label gedruckt',
  in_transit: 'Unterwegs',
  out_for_delivery: 'In Zustellung',
  delivered: 'Zugestellt',
  exception: 'Problem',
  returned: 'Retoure',
  unknown: 'Unbekannt',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAddress(addr: Record<string, any> | null | undefined): string {
  if (!addr) return '–';
  // Adresse ist nur ein Stub (nur addressId, noch nicht hydriert)
  if (typeof addr === 'object' && addr.addressId && !addr.name1 && !addr.postCode) {
    return `ID ${addr.addressId} (wird beim Oeffnen geladen…)`;
  }
  const parts: string[] = [];
  const name = [addr.name1, addr.name2].filter(Boolean).join(' ').trim();
  if (name) parts.push(name);
  const place = [addr.postCode, addr.town].filter(Boolean).join(' ').trim();
  if (addr.street) parts.push(`${addr.street}${addr.houseNumber ? ' ' + addr.houseNumber : ''}`);
  if (place) parts.push(place);
  return parts.length > 0 ? parts.join(', ') : '–';
}

function renderItems(items: OrderListItem['items'], maxShown: number): React.JSX.Element | null {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, maxShown);
  const remaining = items.length - shown.length;
  return (
    <div className="mt-2 text-xs">
      <table className="w-full">
        <tbody className="divide-y divide-[#DAD9D9]">
          {shown.map((item) => (
            <tr key={item.id} className="text-gray-700">
              <td className="py-1 pr-2 font-mono text-gray-600 w-12">{item.quantity ?? '?'}x</td>
              <td className="py-1 pr-2 truncate" title={item.itemName ?? ''}>
                {/* Phase 15: Hersteller-Badge wenn vorhanden */}
                {item.manufacturerName && (
                  <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[10px] font-semibold bg-[#1A1A1A] text-white align-middle">
                    {item.manufacturerName}
                  </span>
                )}
                {item.itemName ?? 'unbenannt'}
              </td>
              <td className="py-1 font-mono text-gray-500 text-[10px] whitespace-nowrap">
                {item.variationNumber ?? `var ${item.variationId}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {remaining > 0 && (
        <div className="text-[11px] text-gray-500 italic mt-1">+{remaining} weitere Position{remaining === 1 ? '' : 'en'}</div>
      )}
    </div>
  );
}

function renderOrderRow(o: OrderListItem, isChild: boolean, showItems: boolean): React.JSX.Element {
  const badge = statusBadge[o.trackingStatus] ?? { label: o.trackingStatus, cls: 'bg-gray-100 text-gray-700' };
  return (
    <div className={`${isChild ? 'pl-4 border-l-2 border-[#DAD9D9]' : ''}`}>
      {/* Zeile 1: Auftragsnr + Status-Pills */}
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span className="font-mono text-base font-semibold text-[#1A1A1A]">#{o.plentyOrderId}</span>
        {(o.orderType === 4 || o.orderType === 2) && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold">
            LIEFERAUFTRAG
          </span>
        )}
        {o.orderStatusName && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-[#E9FF57] text-[#1A1A1A] font-medium">
            {o.orderStatusName}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${badge.cls}`}>
          {badge.label}
        </span>
        {/* Phase 17: Status-Switch-Datum prominent in Zeile 1 — wann wurde der aktuelle Plenty-Status gesetzt? */}
        {(o.supplierStatusSince || o.statusEnteredAt) && (
          <span
            className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono"
            title="Datum des Wechsels in den aktuellen Plenty-Status"
          >
            seit {formatDate(o.supplierStatusSince ?? o.statusEnteredAt)}
          </span>
        )}
      </div>

      {/* Zeile 2: Stammdaten kompakt */}
      <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-0.5 mb-1.5">
        <span>Eingang: <span className="text-[#1A1A1A] font-medium">{formatDate(o.orderReceivedAt)}</span></span>
        {o.contactId && <span>Kunde: <span className="text-[#1A1A1A] font-medium">{o.contactId}</span></span>}
        {o.paymentMethodName && (
          <span className="flex items-center gap-1">
            <CreditCard className="w-3 h-3" />
            <span className="text-[#1A1A1A]">{o.paymentMethodName}</span>
          </span>
        )}
        {o.orderSource && <span>Quelle: <span className="text-[#1A1A1A]">{o.orderSource}</span></span>}
      </div>

      {/* Zeile 3: Adressen */}
      <div className="text-xs text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 mb-1.5">
        <span className="flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><span className="text-gray-400">Rechnung:</span> <span className="text-[#1A1A1A]">{formatAddress(o.billingAddress)}</span></span>
        </span>
        <span className="flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><span className="text-gray-400">Lieferung:</span> <span className="text-[#1A1A1A]">{formatAddress(o.shippingAddress)}</span></span>
        </span>
      </div>

      {/* Zeile 4: Versand + Tags */}
      <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 mb-1.5">
        <span className="flex items-center gap-1">
          <Truck className="w-3 h-3" />
          <span className="text-[#1A1A1A]">{o.shippingProfileName ?? (o.shippingProfileId ? `Profil ${o.shippingProfileId}` : '–')}</span>
        </span>
        {/* Phase 2: Paketnummern mit per-Paket-Status (Mehrpaket-Sendungen). */}
        {(() => {
          const statuses = o.packageStatuses && o.packageStatuses.length > 0
            ? o.packageStatuses
            : ((o.packageNumbers && o.packageNumbers.length > 0)
                ? o.packageNumbers.map((pn) => ({ packageNumber: pn, internalStatus: null as string | null }))
                : (o.packageNumber ? [{ packageNumber: o.packageNumber, internalStatus: null as string | null }] : []));
          if (statuses.length === 0) return null;
          const badge = (st: string | null) => {
            if (st === 'exception') return { txt: '⚠ Problem', cls: 'text-red-700' };
            if (st === 'delivered') return { txt: '✓ zugestellt', cls: 'text-green-700' };
            if (st === 'returned') return { txt: '↩ Retoure', cls: 'text-orange-700' };
            return null;
          };
          return (
            <span className="flex items-center gap-2 flex-wrap">
              {statuses.length > 1 && (
                <span className="text-[10px] text-gray-500">{statuses.length} Pakete:</span>
              )}
              {statuses.map((p) => {
                const b = badge(p.internalStatus);
                return (
                  <span key={p.packageNumber} className="inline-flex items-center gap-1">
                    <span className="font-mono text-[11px] text-[#1A1A1A]">{p.packageNumber}</span>
                    {b && <span className={`text-[10px] font-semibold ${b.cls}`}>{b.txt}</span>}
                  </span>
                );
              })}
            </span>
          );
        })()}
        {/* Phase 2: Auftrags-Level Problem-Hinweis wenn ein Paket entsorgt/beschaedigt ist */}
        {o.hasProblem && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-semibold">
            <AlertCircle className="w-3 h-3" /> Paket-Problem — Ersatz/Gutschrift prüfen
          </span>
        )}
        {o.tags && o.tags.length > 0 && (
          <span className="flex items-center gap-1 flex-wrap">
            <Tag className="w-3 h-3" />
            {o.tags.map((t) => (
              <span key={t.id} className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px]">
                {t.name}
              </span>
            ))}
          </span>
        )}
        {o.warenausgangBookedAt && (
          <span className="text-[11px] text-gray-500">Warenausgang: {formatDateTime(o.warenausgangBookedAt)}</span>
        )}
      </div>

      {/* Zeile 5: Carrier-Event — mit DHL-Klartext + Sendungslage-Badge (24.08.2026).
          Das 7er-Enum sagt bei den wichtigsten Faellen nur "Unbekannt"; DHLs
          eigener Text sagt was wirklich los ist. Rein Anzeige, internal_status
          bleibt unangetastet. */}
      {o.lastInternalStatus && (() => {
        const badge = deriveSendungslageBadge(
          o.lastEventCode, o.lastEventDescription, o.lastIstRuecksendung ?? null,
        );
        const klartext = cleanEventText(o.lastEventDescription);
        return (
          <div className="text-xs text-gray-700 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                Letzter Event:{' '}
                <span className="font-medium">
                  {internalStatusLabel[o.lastInternalStatus] ?? o.lastInternalStatus}
                </span>
                {o.lastEventCode && <span className="text-gray-400 font-mono"> [{o.lastEventCode}]</span>}
                {o.lastEventAt && <span className="text-gray-500"> · {formatDateTime(o.lastEventAt)}</span>}
              </span>
              {badge && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${SENDUNGSLAGE_TONE_CLS[badge.tone]}`}>
                  {badge.label}
                </span>
              )}
            </div>
            {klartext && (
              <div className="text-[11px] text-gray-500 italic mt-0.5">„{klartext}“</div>
            )}
          </div>
        );
      })()}

      {/* Artikel-Tabelle inline */}
      {showItems && renderItems(o.items, 3)}
    </div>
  );
}

export default function OrderCard({ group, kategorie, onSelect, onFollowupToggled, onClosed, showGeklaertToggle, onGeklaertToggled }: OrderCardProps) {
  const [showChildren, setShowChildren] = useState(false);
  // Phase 14: Optimistic-UI fuer Followup-Toggle — DB-Persistierung im Hintergrund
  const [followupMarked, setFollowupMarked] = useState<boolean>(group.main.followupMarkedAt != null);
  const [followupSaving, setFollowupSaving] = useState(false);
  // 20.08.2026: Geklärt-Haken (Rücksendungs-Reiter) — Optimistic-UI wie Followup
  const [geklaert, setGeklaert] = useState<boolean>(group.main.ruecksendungGeklaertAt != null);
  const [geklaertSaving, setGeklaertSaving] = useState(false);
  // Manueller Abschluss (mit Bestaetigung)
  const [closing, setClosing] = useState(false);
  const alreadyClosed =
    group.main.trackingStatus === 'done' || group.main.trackingStatus === 'closed_manually';
  const stuck = group.main.hasOpenNotification;
  // 18.06.2026: verwaister Lieferauftrag = Typ 2 (Lieferschein) ODER Typ 4, Parent nicht in Page
  const isOrphanType4 =
    (group.main.orderType === 4 || group.main.orderType === 2) &&
    !group.main.parentInScope &&
    group.main.parentPlentyOrderId != null;

  // Phase 5: Spedition-Alarm-Farbe (gelb 5W, rot 6W).
  // 18.06.2026: Anker-Regel — Hauptauftrag mit Lieferauftrag wird ueber das Kind bewertet.
  const nowMs = Date.now();
  const alarm =
    kategorie === 'spedition' || kategorie === 'lieferanten'
      ? computeSpeditionAlarm(group, nowMs)
      : null;
  // 19.06.2026: exakte Wochenanzahl im Banner (Farb-Cluster bleibt: gelb >=5W, rot >=6W).
  const alarmDauer = alarm
    ? formatDurationHuman(Math.floor((nowMs - alarm.sinceMs) / 3_600_000))
    : null;

  // Border-Styles je nach Alarm
  let borderCls = 'border border-[#DAD9D9]';
  if (alarm?.level === 'red') borderCls = 'border-l-4 border-l-red-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9] bg-red-50/30';
  else if (alarm?.level === 'yellow') borderCls = 'border-l-4 border-l-yellow-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9] bg-yellow-50/30';
  else if (stuck) borderCls = 'border-l-4 border-l-orange-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9]';
  else if (followupMarked) borderCls = 'border-l-4 border-l-amber-400 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9] bg-amber-50/30';

  async function handleFollowupToggle(ev: React.MouseEvent) {
    ev.stopPropagation();
    if (followupSaving) return;
    const nextMarked = !followupMarked;
    setFollowupMarked(nextMarked);
    setFollowupSaving(true);
    try {
      await api.patch(`/programmes/auftragsverfolgung/orders/${group.main.id}/followup`, {
        marked: nextMarked,
      });
      onFollowupToggled?.(group.main.id, nextMarked);
    } catch {
      // Rollback bei Fehler
      setFollowupMarked(!nextMarked);
    } finally {
      setFollowupSaving(false);
    }
  }

  async function handleGeklaertToggle(ev: React.MouseEvent) {
    ev.stopPropagation();
    if (geklaertSaving) return;
    const next = !geklaert;
    setGeklaert(next);
    setGeklaertSaving(true);
    try {
      await api.patch(`/programmes/auftragsverfolgung/orders/${group.main.id}/ruecksendung-geklaert`, {
        geklaert: next,
      });
      onGeklaertToggled?.(group.main.id);
    } catch {
      // Rollback bei Fehler
      setGeklaert(!next);
    } finally {
      setGeklaertSaving(false);
    }
  }

  async function handleClose(ev: React.MouseEvent) {
    ev.stopPropagation();
    if (closing || alreadyClosed) return;
    const ok = window.confirm(`Auftrag #${group.main.plentyOrderId} wirklich abschliessen?`);
    if (!ok) return;
    setClosing(true);
    try {
      await api.post(`/programmes/auftragsverfolgung/close/${group.main.id}`, {
        reason: 'manual',
      });
      onClosed?.(group.main.id);
    } catch (err: any) {
      // Bereits geschlossen (400) o.ae. — trotzdem reload, damit UI konsistent wird
      if (err?.response?.status === 400) {
        onClosed?.(group.main.id);
      } else {
        window.alert('Abschliessen fehlgeschlagen. Bitte erneut versuchen.');
      }
    } finally {
      setClosing(false);
    }
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden relative ${borderCls}`}
      onClick={() => onSelect(group.main.id)}
    >
      <div className="p-4">
        {/* Alarm-Banner Spedition — exakte Dauer, Farbe nach Cluster (gelb 5W / rot 6W) */}
        {alarm?.level === 'red' && (
          <div className="flex items-center gap-1.5 text-xs text-red-700 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            {alarmDauer} im Spedition-Status — Hersteller anrufen!
          </div>
        )}
        {alarm?.level === 'yellow' && (
          <div className="flex items-center gap-1.5 text-xs text-yellow-800 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            {alarmDauer} im Spedition-Status
          </div>
        )}

        {/* Stuck-Banner */}
        {stuck && !alarm && (
          <div className="flex items-center gap-1.5 text-xs text-orange-700 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            Stuck — offene Notification
          </div>
        )}

        {/* Phase 12+13: Stuck-Info-Zeile (DHL oder Lieferant je nach kind) */}
        {stuck && !alarm && group.main.stuckMeta != null && (
          <div className="text-xs text-orange-600 mb-2 font-mono ml-5">
            {group.main.stuckMeta.kind === 'supplier' && group.main.stuckMeta.supplierName
              ? formatSupplierStuckInfo(group.main.stuckMeta, group.main.stuckMeta.supplierName)
              : formatStuckInfo(group.main.stuckMeta)}
          </div>
        )}

        {/* Lieferanten-Tab: "Bei <Lieferant> seit X" — IMMER zeigen wenn die
            Order in 5.50/5.60/5.65/5.66 ist UND noch keine offene Stuck-Notification
            existiert (sonst ist die Zeile redundant zur stuckMeta-Zeile oben). */}
        {(kategorie === 'lieferanten' || kategorie === 'spedition') &&
          !stuck &&
          group.main.supplierStatusSince != null && (() => {
            const supplierKey = supplierKeyForStatus(group.main.orderStatusId);
            if (!supplierKey) return null;
            const since = formatSupplierStatusSince(group.main.supplierStatusSince, supplierKey);
            return (
              <div className="text-xs text-gray-600 mb-2 ml-5">{since}</div>
            );
          })()}

        {/* Verwaister Type-4 Cross-Link */}
        {isOrphanType4 && (
          <div className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded mb-2">
            {/* 20.08.2026: neutraler Text — seit dem Eltern-Kind-Dedup ist der
                Hauptauftrag im Lieferanten-Reiter bewusst ausgeblendet, nicht
                zwingend ausserhalb des Tracking-Zeitraums. */}
            ↗ gehört zu Auftrag #{group.main.parentPlentyOrderId}
          </div>
        )}

        {/* Aktionen oben rechts: Abschliessen-Button + Followup-Stern */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {!alreadyClosed && (
            <button
              type="button"
              onClick={handleClose}
              disabled={closing}
              aria-label="Auftrag abschliessen"
              title="Auftrag manuell abschliessen"
              className="flex items-center gap-1 px-2 py-1 rounded border border-green-200 bg-green-50 text-green-700 text-xs font-medium transition-colors hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
              {closing ? 'Schliesse…' : 'Abschliessen'}
            </button>
          )}

          {/* 20.08.2026: Geklärt-Haken — nur im Rücksendungs-Reiter. Ein geklaerter
              Fall verschwindet aus der Default-Ansicht (Filter blendet ihn aus)
              und zaehlt nicht mehr im Badge. */}
          {showGeklaertToggle && (
            <button
              type="button"
              onClick={handleGeklaertToggle}
              disabled={geklaertSaving}
              aria-label={geklaert ? 'Klärung zurücknehmen' : 'Als geklärt markieren'}
              title={geklaert
                ? 'Klärung zurücknehmen (Fall wieder als offen zeigen)'
                : 'Als geklärt markieren (Gutschrift/Neuversand erledigt)'}
              className={`flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                geklaert
                  ? 'border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                  : 'border-[#DAD9D9] bg-white text-gray-500 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
              {geklaertSaving ? 'Speichere…' : geklaert ? 'Geklärt ✓' : 'Geklärt?'}
            </button>
          )}

          {/* 27.08.2026: Automatisch geklaerte Faelle zeigen ihren Beleg —
              sonst waere nicht nachvollziehbar, warum ein Fall aus der Liste
              verschwunden ist. Von Hand gesetzte Haken haben keinen Grund. */}
          {showGeklaertToggle && geklaert && group.main.ruecksendungGeklaertGrund && (
            <span
              className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
              title="Automatisch geklärt — der Vorgang existiert bereits in Plenty"
            >
              {group.main.ruecksendungGeklaertGrund}
            </span>
          )}

          {/* Phase 14: Followup-Toggle (Stern) */}
          <button
            type="button"
            onClick={handleFollowupToggle}
            disabled={followupSaving}
            aria-label={followupMarked ? 'Nachverfolgung entfernen' : 'Zur Nachverfolgung markieren'}
            title={followupMarked ? 'Nachverfolgung entfernen' : 'Zur Nachverfolgung markieren'}
            className={`p-1.5 rounded transition-colors ${
              followupMarked
                ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Star
              className={`w-5 h-5 ${followupMarked ? 'fill-amber-400' : ''}`}
              strokeWidth={1.75}
            />
          </button>
        </div>

        {/* Phase 14: Plenty-Notizen-Snippet (nur das aelteste/neueste anzeigen) */}
        {group.main.plentyComments && group.main.plentyComments.length > 0 && (
          <div className="flex items-start gap-1.5 text-xs text-[#1A1A1A] bg-amber-50/60 border border-amber-200 rounded px-2 py-1.5 mb-2 pr-40">
            <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-amber-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-amber-900">
                Plenty-Notiz{group.main.plentyComments.length > 1 ? `n (${group.main.plentyComments.length})` : ''}:
              </span>{' '}
              {/* Aktuellste Notiz zuerst (sortiert nach createdAt DESC, sonst Reihenfolge aus Plenty) */}
              {(() => {
                const sorted = [...group.main.plentyComments].sort((a, b) => {
                  const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                  const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                  return tb - ta;
                });
                const latest = sorted[0];
                const text = (latest.text || '').replace(/<[^>]+>/g, '').trim();
                const truncated = text.length > 140 ? `${text.slice(0, 140)}…` : text;
                return <span className="text-gray-700">{truncated}</span>;
              })()}
            </div>
          </div>
        )}

        {/* Haupt-Auftrag */}
        {renderOrderRow(group.main, false, true)}

        {/* Eingeklappte Type-4-Kinder */}
        {group.children.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#DAD9D9]">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowChildren((v) => !v);
              }}
              className="flex items-center gap-1 text-xs text-[#1A1A1A] hover:underline"
            >
              {showChildren ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {group.children.length}{' '}
              {group.children.length === 1 ? 'Lieferauftrag' : 'Lieferauftraege'}{' '}
              {showChildren ? 'ausblenden' : 'anzeigen'}
            </button>
            {showChildren && (
              <div className="mt-2 space-y-3">
                {group.children.map((child) => (
                  <div
                    key={child.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(child.id);
                    }}
                  >
                    {renderOrderRow(child, true, false)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
